import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, IconButton, CircularProgress, Alert, Toolbar, TextField, InputAdornment, Pagination, PaginationItem, FormControl, Select, MenuItem, Typography
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import api from '../services/api';
import PixKeyModal from './PixKeyModal';

const PixTable = () => {
  const [data, setData] = useState({
    pixKeys: [],
    totalPixKeys: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1); // Ajustado para 1-indexed
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPixKey, setCurrentPixKey] = useState(null);

  const fetchPixKeys = useCallback(async () => {
    try {
      const response = await api.get(`/bank_data?page=${page}&limit=${rowsPerPage}`);
      setData({
        pixKeys: response.data,
        totalPixKeys: response.data.length // Ajuste se a resposta não contiver o total
      });
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch pix keys', error);
      setError('Failed to fetch pix keys');
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchPixKeys();
  }, [fetchPixKeys]);

  const handleEditPixKey = (pixKey) => {
    setCurrentPixKey(pixKey);
    setModalOpen(true);
  };

  const handleDeletePixKey = (pixKey) => {
    Swal.fire({
      title: 'Você tem certeza?',
      text: `Deseja realmente excluir a chave Pix ${pixKey.chavepix}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir!',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/bank_data/${pixKey.id}`);
          setData((prevData) => ({
            pixKeys: prevData.pixKeys.filter((p) => p.id !== pixKey.id),
            totalPixKeys: prevData.totalPixKeys - 1
          }));
          Swal.fire('Excluído!', 'A chave Pix foi excluída.', 'success');
          fetchPixKeys();
        } catch (error) {
          Swal.fire('Erro!', 'Falha ao excluir a chave Pix.', 'error');
        }
      }
    });
  };

  const handleAddPixKey = () => {
    setCurrentPixKey(null);
    setModalOpen(true);
  };

  const handleSavePixKey = async (pixKey) => {
    try {
      if (pixKey.id) {
        await api.put(`/bank_data/${pixKey.id}`, pixKey);
        setData((prevData) => ({
          pixKeys: prevData.pixKeys.map((p) => (p.id === pixKey.id ? pixKey : p)),
          totalPixKeys: prevData.totalPixKeys
        }));
      } else {
        const response = await api.post('/bank_data', pixKey);
        setData((prevData) => ({
          pixKeys: [response.data, ...prevData.pixKeys].slice(0, 4),
          totalPixKeys: prevData.totalPixKeys + 1
        }));
      }
      setModalOpen(false);
      Swal.fire('Sucesso!', 'Chave Pix salva com sucesso.', 'success');
      fetchPixKeys();
    } catch (error) {
      Swal.fire('Erro!', 'Falha ao salvar a chave Pix.', 'error');
    }
  };

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredPixKeys = data.pixKeys ? data.pixKeys.filter(pixKey =>
    pixKey.chavepix.toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Roboto' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Roboto' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <Box className="pix-keys-container" sx={{ width: '50%', padding: 3, backgroundColor: 'white', borderRadius: 2, boxShadow: 3, fontFamily: 'Roboto' }}>
        <Typography variant="h4" sx={{ textAlign: 'center', marginBottom: 3 }}>Lista de Chaves Pix</Typography>
        <Toolbar sx={{ height: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
          <TextField
            placeholder="Buscar..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ width: '30%', backgroundColor: 'white' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              style: { fontFamily: 'Roboto' },
            }}
            InputLabelProps={{ style: { fontFamily: 'Roboto' } }}
          />
          <FormControl sx={{ minWidth: 60 }}>
            <Select
              labelId="rows-per-page-label"
              id="rows-per-page"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              sx={{ height: '40px', fontFamily: 'Roboto' }}
              inputProps={{ style: { fontFamily: 'Roboto' } }}
            >
              <MenuItem value={5} sx={{ fontFamily: 'Roboto' }}>5</MenuItem>
              <MenuItem value={10} sx={{ fontFamily: 'Roboto' }}>10</MenuItem>
              <MenuItem value={25} sx={{ fontFamily: 'Roboto' }}>25</MenuItem>
            </Select>
          </FormControl>
        </Toolbar>
        <TableContainer component={Paper} sx={{ boxShadow: 'none', width: '100%' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">Nome</TableCell>
                <TableCell align="center">Chave Pix</TableCell>
                <TableCell align="center">Valor</TableCell>
                <TableCell align="center">Cidade</TableCell>
                <TableCell align="center">
                  Actions
                  <IconButton color="primary" aria-label="add pix key" onClick={handleAddPixKey} style={{ marginLeft: '10px' }}>
                    <AddIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPixKeys.map((pixKey) => (
                <TableRow key={pixKey.id}>
                  <TableCell align="center">{pixKey.nome}</TableCell>
                  <TableCell align="center">{pixKey.chavepix}</TableCell>
                  <TableCell align="center">{pixKey.valor}</TableCell>
                  <TableCell align="center">{pixKey.cidade}</TableCell>
                  <TableCell align="center">
                    <IconButton color="primary" aria-label="edit pix key" onClick={() => handleEditPixKey(pixKey)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="secondary" aria-label="delete pix key" onClick={() => handleDeletePixKey(pixKey)} style={{ marginLeft: '10px' }}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={Math.ceil(data.totalPixKeys / rowsPerPage)}
            page={page}
            onChange={handleChangePage}
            renderItem={(item) => (
              <PaginationItem
                {...item}
                sx={{ '&.Mui-selected': { backgroundColor: 'primary.main', color: 'white', fontFamily: 'Roboto' } }}
              />
            )}
          />
        </Box>
      </Box>
      <PixKeyModal open={modalOpen} onClose={() => setModalOpen(false)} onSave={handleSavePixKey} pixKey={currentPixKey} />
    </Box>
  );
};

export default PixTable;
